import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";

type CanvasReadbackDetails = {
	readonly imageSize: GenericSize<number>;
	readonly canvasSize: GenericSize<number>;
};

// Browser memory hints for Sentry. `deviceMemory` is Chromium-only; on WebKit
// (where canvas budget failures actually happen) it is undefined, so the
// hardware concurrency is included as the next-best size-of-device signal.
function describeDeviceForCanvas() {
	const nav = typeof navigator === "undefined" ? undefined : navigator;
	const deviceMemory = (nav as { deviceMemory?: number } | undefined)
		?.deviceMemory;
	return `deviceMemory=${deviceMemory ?? "n/a"} cores=${nav?.hardwareConcurrency ?? "n/a"}`;
}

function describeSize({ width, height }: GenericSize<number>) {
	return `${width}x${height}`;
}

// Thrown when a 2D canvas hands back no pixels. WebKit enforces a per-page
// canvas memory budget; past it, drawing silently no-ops and getImageData
// returns all zeros (including alpha) rather than throwing. A real decoded
// photo always has opaque alpha, so all-zero alpha is a definite failure, not
// a legitimately blank image.
class CanvasReadbackError extends Error {
	readonly details: CanvasReadbackDetails;

	constructor(details: CanvasReadbackDetails) {
		super(
			`Canvas readback returned no pixels (image ${describeSize(details.imageSize)}, canvas ${describeSize(details.canvasSize)}, ${describeDeviceForCanvas()})`,
		);
		this.name = "CanvasReadbackError";
		this.details = details;
	}
}

// Thrown when canvas.toBlob yields nothing for every encoding tried. Same
// WebKit budget failure as above, surfacing at the encode step instead.
class CanvasEncodeError extends Error {
	constructor(canvasSize: GenericSize<number>) {
		super(
			`Unable to encode canvas to an image (canvas ${describeSize(canvasSize)}, ${describeDeviceForCanvas()})`,
		);
		this.name = "CanvasEncodeError";
	}
}

export type { CanvasReadbackDetails };
export { CanvasReadbackError, CanvasEncodeError, describeDeviceForCanvas };
